// src/components/MobileMapOverlay.jsx
import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import LeafletMap from "./LeafletMap";

/**
 * This overlay appears on mobile devices, showing a full-screen Leaflet map.
 *
 * Props:
 *  - markers (Array): each entry { id, lat, lng, popupText }
 *  - defaultLat, defaultLng, defaultZoom (number): fallback map positioning
 *  - spotRefs (ref): for scrolling text into view when a marker is clicked
 *
 * No functionalities removed; only additional highlight logic
 * is added so it matches the left-side Leaflet map behavior.
 */
function MobileMapOverlay(
  {
    markers = [],
    defaultLat = 49.2827,
    defaultLng = -123.1207,
    defaultZoom = 12,
    spotRefs,
  },
  ref
) {
  const mapInstanceRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileMapButton, setShowMobileMapButton] = useState(false);
  const [showMobileMapOverlay, setShowMobileMapOverlay] = useState(false);

  // If user wants to focus the map at a specific lat/lng
  const [markerToFocus, setMarkerToFocus] = useState(null);

  // Controls whether we skip auto-fit
  const [mobileSkipAutoFit, setMobileSkipAutoFit] = useState(false);

  // We track which text was previously highlighted so we can reset color:
  const oldActiveMarkerIdRef = useRef(null);

  const [lastScrollPos, setLastScrollPos] = useState(0);

  // Listen for window resize => update isMobile
  // Listen for scroll => show/hide map button if user scrolls downward
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    const handleScroll = () => {
      const currentPos = window.pageYOffset;
      const goingDown = currentPos > lastScrollPos;
      setShowMobileMapButton(goingDown);
      setLastScrollPos(currentPos);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollPos]);

  // Open the overlay => reset skipAutoFit = false so it auto-fits again
  const handleOpenMapOverlay = () => {
    setMobileSkipAutoFit(false);
    setShowMobileMapOverlay(true);
  };

  // Closes the overlay
  const closeMobileMapOverlay = () => {
    setShowMobileMapOverlay(false);
  };

  const stopOverlayPropagation = (e) => {
    e.stopPropagation();
  };

  // ============ Marker Click Logic ============
  // 1) The LeafletMap will call onMarkerClick(markerId) when a marker is clicked.
  // 2) Here we replicate the highlight logic from the left-side Leaflet map.
  // 3) Then we close the overlay (so user sees the scrolled text).
  const onMarkerClick = (markerId) => {
    // Highlight this text (same color as in LeafletMap)
    //  -- reset old highlight
    if (oldActiveMarkerIdRef.current && spotRefs?.current?.[oldActiveMarkerIdRef.current]) {
      spotRefs.current[oldActiveMarkerIdRef.current].style.color = "";
    }
    //  -- set new highlight
    if (markerId && spotRefs?.current?.[markerId]) {
      spotRefs.current[markerId].style.color = "#ff6b6b";
    }
    // Remember the newly highlighted marker
    oldActiveMarkerIdRef.current = markerId;

    // Scroll text into view
    setMobileSkipAutoFit(true);
    const targetRef = spotRefs.current[markerId];
    if (targetRef && targetRef.scrollIntoView) {
      targetRef.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // Finally, close the overlay on mobile
    if (isMobile && showMobileMapOverlay) {
      closeMobileMapOverlay();
    }
  };

  // Provide a method so parent can “focus on marker”
  useImperativeHandle(ref, () => ({
    focusOnMarker(lat, lng, desktopMapRef) {
      if (isMobile) {
        setMarkerToFocus({ lat, lng });
        setMobileSkipAutoFit(true);
        setShowMobileMapOverlay(true);
      } else if (desktopMapRef?.current) {
        desktopMapRef.current.flyTo([lat, lng], 14, { duration: 1.5 });
      }
    },
  }));

  // If overlay opens & we have markerToFocus => flyTo it
  useEffect(() => {
    if (isMobile && showMobileMapOverlay && markerToFocus && mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([markerToFocus.lat, markerToFocus.lng], 14, {
        duration: 1.5,
      });
      setMarkerToFocus(null);
    }
  }, [isMobile, showMobileMapOverlay, markerToFocus]);

  return (
    <>
      {/* Floating "Open Map" button (mobile only, hidden if overlay is open) */}
      {isMobile && showMobileMapButton && !showMobileMapOverlay && (
        <button
          className="mobile-map-button"
          onClick={handleOpenMapOverlay}
          aria-label="Open full-screen map"
        >
          <i className="fas fa-map-marked-alt"></i>
        </button>
      )}

      {/* Fullscreen overlay (mobile) */}
      {isMobile && showMobileMapOverlay && (
        <div className="mobile-map-overlay" onClick={closeMobileMapOverlay}>
          <div
            className="mobile-map-overlay-content"
            onClick={stopOverlayPropagation}
          >
            <LeafletMap
              lat={defaultLat}
              lng={defaultLng}
              zoom={defaultZoom}
              markers={markers.map((m) => ({
                lat: m.lat,
                lng: m.lng,
                popupText: m.popupText,
                markerId: m.id,
              }))}
              onMarkerClick={onMarkerClick}  // triggers highlight + overlay close
              mapRef={mapInstanceRef}
              skipAutoFit={mobileSkipAutoFit}
              // We still pass spotRefs so that LeafletMap itself can also do highlight,
              // but we already replicate that logic here to guarantee it fires before unmount
              spotRefs={spotRefs}
              // once user pans/zooms => disable auto-fit
              onUserInteraction={() => setMobileSkipAutoFit(true)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default forwardRef(MobileMapOverlay);